import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFileExcel, FaChartBar, FaSyncAlt, FaTimes, FaUpload, FaCheckCircle, FaExclamationCircle, FaSpinner } from 'react-icons/fa';
import { useTheme } from '../../context/SocketContext';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, BarElement, CategoryScale, LinearScale,
  PointElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import * as XLSX from 'xlsx';

ChartJS.register(
  BarElement, CategoryScale, LinearScale,
  PointElement, Title, Tooltip, Legend, Filler
);

const COLORS = [
  'rgba(54, 162, 235, 0.7)',
  'rgba(255, 99, 132, 0.7)',
  'rgba(75, 192, 192, 0.7)',
  'rgba(255, 206, 86, 0.7)',
  'rgba(153, 102, 255, 0.7)',
  'rgba(255, 159, 64, 0.7)'
];

const SPECIAL_BOOKKEEPING_USER_IDS = ['691c4908d1fab55e7f2c2ef5', '690c8991d6e0a0f153191140'];
const BOOKKEEPING_STEPS = [
  {
    title: 'Step 1',
    description: 'Upload the Bank / Credit Card & Other Documents'
  },
  {
    title: 'Step 2',
    description: 'Nature of Transaction Required'
  },
  {
    title: 'Step 3',
    description: 'Financial Report'
  }
];

const UserBookkeeping = () => {
  const { currentThemeColors } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookkeepingData, setBookkeepingData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [activeStepIndex, setActiveStepIndex] = useState(null);

  useEffect(() => {
    fetchBookkeepingData();
  }, []);

  const fetchBookkeepingData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setIsRefreshing(true);
      setError('');

      const userResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!userResponse.ok) throw new Error('Failed to fetch user data');
      const userDataResponse = await userResponse.json();
      setUserData(userDataResponse.user);

      const bookkeepingResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${userDataResponse.user._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!bookkeepingResponse.ok) {
        if (bookkeepingResponse.status === 404) {
          setBookkeepingData(null);
          return;
        }
        throw new Error('Failed to fetch bookkeeping data');
      }

      const data = await bookkeepingResponse.json();
      setBookkeepingData(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isUploadStep = (index) => [0, 2].includes(index);

  const handleStepClick = (stepIndex) => {
    if (isUploadStep(stepIndex) && showSpecialSteps) {
      setActiveStepIndex(stepIndex);
      setShowUploadModal(true);
      setUploadMessage('');
      setUploadError('');
    }
  };

  const handleCloseUploadModal = () => {
    if (uploading) return;
    setShowUploadModal(false);
    setActiveStepIndex(null);
    setSelectedFiles([]);
    setUploadMessage('');
    setUploadError('');
  };

  const handleFileSelection = (event) => {
    setSelectedFiles(Array.from(event.target.files || []));
    setUploadMessage('');
    setUploadError('');
  };

  const handleUploadSubmit = async () => {
    if (!selectedFiles.length) {
      setUploadError('Please select at least one file.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    if (!userData?._id) {
      setUploadError('User information is missing. Please refresh and try again.');
      return;
    }

    try {
      setUploading(true);
      setUploadError('');
      setUploadMessage('');

      const formData = new FormData();
      selectedFiles.forEach(file => formData.append('files', file));
      if (activeStepIndex !== null) {
        formData.append('step', BOOKKEEPING_STEPS[activeStepIndex].title);
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${userData._id}/files`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to upload files.');
      }

      const data = await response.json();
      setUploadMessage(data.message || 'Files uploaded successfully.');
      setSelectedFiles([]);
      await fetchBookkeepingData();
    } catch (uploadErr) {
      setUploadError(uploadErr.message || 'Failed to upload files.');
    } finally {
      setUploading(false);
    }
  };

  const exportToExcel = () => {
    if (!bookkeepingData) return;
    
    const worksheet = XLSX.utils.json_to_sheet(
      bookkeepingData.rows.filter(row => !row.include)
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bookkeeping Data");
    XLSX.writeFile(workbook, `${bookkeepingData.title || 'bookkeeping_data'}.xlsx`);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'top',
        labels: {
          font: {
            family: 'Arial',
            size: 12,
            weight: 'bold'
          },
          padding: 20,
          usePointStyle: true,
        }
      },
      title: {
        display: true,
        text: bookkeepingData?.title || 'Bookkeeping Data',
        font: { 
          size: 18,
          family: 'Arial',
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        padding: 10,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            return ` $${context.parsed.y.toLocaleString()}`;
          }
        }
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart',
    },
    elements: {
      bar: {
        borderRadius: 6,
        borderSkipped: 'bottom',
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: {
          font: {
            weight: 'bold'
          }
        }
      },
      y: {
        beginAtZero: false,
        grid: {
          color: 'rgba(0,0,0,0.05)',
          drawBorder: false
        },
        ticks: {
          callback: value => '$' + value.toLocaleString(),
          font: {
            weight: 'bold'
          }
        },
      },
    },
    layout: {
      padding: {
        left: 20,
        right: 20,
        top: 20,
        bottom: 20
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <FaSpinner className="w-12 h-12 text-primary-500 animate-spin mx-auto" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading your financial data...</p>
        </div>
      </div>
    );
  }

  const showSpecialSteps = userData && SPECIAL_BOOKKEEPING_USER_IDS.includes(userData._id);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '0ms', animationFillMode: 'forwards' }}>
        <div>
          <h1 className="text-2xl font-bold text-dark-800 dark:text-white">
            <span style={{ color: 'var(--primary-500)' }}>Bookkeeping</span>
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">View and manage your financial records</p>
        </div>
        <button
          onClick={fetchBookkeepingData}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-white hover:scale-105 disabled:opacity-50 transition-all duration-300"
          style={{ 
            background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
            boxShadow: `0 10px 15px -3px var(--glow-color)`
          }}
        >
          <FaSyncAlt className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Bundle Activation Message */}
      {userData?.isBundlePurchased && (
        <div className="p-4 rounded-xl bg-green-500 text-white flex items-center gap-3 shadow-lg">
          <FaCheckCircle className="w-6 h-6 flex-shrink-0" />
          <span className="font-semibold">1099 Form is Activated - 4 Nos</span>
        </div>
      )}

      {/* Bookkeeping Steps */}
      {showSpecialSteps && (
        <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 rounded-full bg-primary-500 text-white text-sm font-medium">Bookkeeping Process</span>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Complete the steps in sequence to keep your bookkeeping bundle on track.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {BOOKKEEPING_STEPS.map((step, index) => (
              <div
                key={step.title}
                onClick={() => handleStepClick(index)}
                className={`p-4 rounded-xl border border-primary-200 dark:border-primary-800/30 bg-gradient-to-br from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 ${isUploadStep(index) ? 'cursor-pointer hover:shadow-md' : ''} transition-all`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold">
                    {index + 1}
                  </div>
                  <h3 className="font-semibold text-primary-600 dark:text-primary-400">{step.title}</h3>
                </div>
                <p className="text-sm font-medium text-dark-800 dark:text-white">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <FaExclamationCircle className="w-5 h-5" />
            <span><strong>Error:</strong> {error}</span>
          </div>
          <button
            onClick={fetchBookkeepingData}
            disabled={isRefreshing}
            className="px-4 py-2 rounded-lg border border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
          >
            {isRefreshing ? 'Refreshing...' : 'Try Again'}
          </button>
        </div>
      )}

      {bookkeepingData ? (
        <div className="space-y-6">
          {/* Financial Records Table */}
          <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
            <div 
              className="p-4 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              style={{ background: `linear-gradient(135deg, var(--primary-500), var(--accent-500))` }}
            >
              <div className="flex items-center gap-2">
                <FaFileExcel className="w-5 h-5" />
                <h2 className="font-semibold">{bookkeepingData.additionalTitle || 'Financial Records'}</h2>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={exportToExcel}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm transition-colors"
                >
                  <FaFileExcel className="w-4 h-4" />
                  <span className="hidden sm:inline">Export Excel</span>
                </button>
                <button
                  onClick={fetchBookkeepingData}
                  disabled={isRefreshing}
                  className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                >
                  <FaSyncAlt className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-dark-700">
                  <tr>
                    {bookkeepingData.headers.map((header, index) => (
                      <th key={index} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                  {bookkeepingData.rows.filter(row => !row.include).map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white dark:bg-dark-800' : 'bg-gray-50 dark:bg-dark-700/50'}>
                      {bookkeepingData.headers.map((header, colIndex) => (
                        <td key={colIndex} className="px-4 py-3 text-sm text-dark-800 dark:text-gray-300">
                          {typeof row[header] === 'number' ? `$${row[header].toLocaleString()}` : row[header]}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {!bookkeepingData.rows.filter(row => !row.include).length && (
                    <tr>
                      <td colSpan={bookkeepingData.headers.length} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        No additional data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart Section */}
          {bookkeepingData.chartData && (
            <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
              <div 
                className="p-4 text-white flex items-center justify-between"
                style={{ background: `linear-gradient(135deg, var(--primary-500), var(--accent-500))` }}
              >
                <div className="flex items-center gap-2">
                  <FaChartBar className="w-5 h-5" />
                  <h2 className="font-semibold">{bookkeepingData.title || 'Financial Overview'}</h2>
                </div>
                <button
                  onClick={fetchBookkeepingData}
                  disabled={isRefreshing}
                  className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                >
                  <FaSyncAlt className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="p-4" style={{ minHeight: '300px', height: '50vh' }}>
                <Bar data={bookkeepingData.chartData} options={chartOptions} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <FaFileExcel className="w-16 h-16 text-primary-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-dark-800 dark:text-white mb-2">No bookkeeping data available</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Please contact your administrator to set up your financial records
          </p>
          <button
            onClick={fetchBookkeepingData}
            disabled={isRefreshing}
            className="px-6 py-3 rounded-xl bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 transition-colors"
          >
            {isRefreshing ? 'Checking...' : 'Check Again'}
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-dark-700">
              <h3 className="text-lg font-semibold text-dark-800 dark:text-white">
                Upload Documents {activeStepIndex !== null ? `(${BOOKKEEPING_STEPS[activeStepIndex].title})` : ''}
              </h3>
              <button onClick={handleCloseUploadModal} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors">
                <FaTimes className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {activeStepIndex === 2
                  ? 'Upload your completed Financial Report documents (Step 3).'
                  : 'Please upload your Bank / Credit Card statements and other supporting documents (Step 1).'}
              </p>
              <div className="border-2 border-dashed border-gray-200 dark:border-dark-600 rounded-xl p-6 text-center">
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelection}
                  disabled={uploading}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <FaUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Click to select files or drag and drop</p>
                </label>
              </div>
              {selectedFiles.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-dark-800 dark:text-white mb-2">Selected Files</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedFiles.map((file, idx) => (
                      <div key={`${file.name}-${idx}`} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-dark-700">
                        <span className="text-sm text-dark-800 dark:text-gray-300 truncate">{file.name}</span>
                        <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {uploadError && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                  {uploadError}
                </div>
              )}
              {uploadMessage && (
                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-sm">
                  {uploadMessage}
                </div>
              )}
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-100 dark:border-dark-700">
              <button
                onClick={handleCloseUploadModal}
                disabled={uploading}
                className="flex-1 px-4 py-2 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600 disabled:opacity-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleUploadSubmit}
                disabled={uploading || !selectedFiles.length}
                className="flex-1 px-4 py-2 rounded-xl bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 transition-colors"
              >
                {uploading ? 'Uploading...' : 'Upload Files'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserBookkeeping;